/**
 * 视频画布
 *
 * 职责：仅作为「最近作品」选中预览的展示位（用户主动从最近作品点缩略图后，把视频装在这里看）。
 * 生成中 / 失败 / 完成的任务不再自动展示在画布——用户在下方的「最近作品」列表里可以直接看到状态。
 *
 * 保留的能力：
 * - 用户从最近作品点缩略图 → 在画布顶部装一段大尺寸预览（含填充提示词 / 填充参考图 / 填充参考视频 / 作为参考视频 / 下载 / 关闭）
 * - 没有选中预览时，画布显示"等待你的第一段视频"占位
 */

import { Download, Type, Images, Film, X, Video as VideoIcon, AlertCircle, Loader2 } from 'lucide-react';
import type { VideoJobDetail, VideoMode } from '../services/videoApi';
import { withAuthToken } from '../../../utils/storageUrl';
import { getVideoModelById } from '../../../apps/board/modelCache';

interface FillSourceMeta {
  prompt: string | null;
  mode: VideoMode;
  referenceImages: string[];
  firstFrameImage: string | null;
  lastFrameImage: string | null;
  videoReferenceUrls: string[];
}

interface VideoCanvasProps {
  /**
   * @deprecated 不再展示，保留 prop 以兼容父组件无须改动；后续清理可移除
   */
  jobs?: VideoJobDetail[];
  /** @deprecated 与 jobs 一并不再使用 */
  onJobClick?: (job: VideoJobDetail) => void;
  /** @deprecated 与 jobs 一并不再使用 */
  onDownload?: (job: VideoJobDetail) => void;
  /**
   * 「最近作品」选中预览
   * 这条数据不放进 jobs 里（避免和当前提交的任务串联），独立渲染在画布顶部
   */
  selectedRecentJob?: VideoJobDetail | null;
  /** 当前模型对各 mode 的支持（控制"填充参考图"按钮可用性） */
  recentModelSupportsImg2Video?: boolean;
  recentModelSupportsFirstLastFrame?: boolean;
  /** 当前模式是否支持参考视频 */
  recentModeSupportsVideoReference?: boolean;
  onCloseRecent?: () => void;
  onFillPromptFromRecent?: (prompt: string) => void;
  onFillReferenceFromRecent?: (source: FillSourceMeta) => void;
  onFillVideoReferenceFromRecent?: (videoUrls: string[]) => void;
  onUseResultAsVideoReferenceFromRecent?: (videoUrl: string) => void;
  onDownloadRecent?: (job: VideoJobDetail) => void;
}

export function VideoCanvas({
  selectedRecentJob,
  recentModelSupportsImg2Video = true,
  recentModelSupportsFirstLastFrame = true,
  recentModeSupportsVideoReference = true,
  onCloseRecent,
  onFillPromptFromRecent,
  onFillReferenceFromRecent,
  onFillVideoReferenceFromRecent,
  onUseResultAsVideoReferenceFromRecent,
  onDownloadRecent,
}: VideoCanvasProps) {
  if (!selectedRecentJob) {
    return (
      <div className="card h-[400px] flex flex-col items-center justify-center text-light-muted">
        <VideoIcon className="w-16 h-16 mb-3 text-light-border" />
        <p className="text-sm">等待你的第一段视频</p>
        <p className="text-xs mt-1">填好左侧参数后点击「生成视频」</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <RecentPreviewCard
        job={selectedRecentJob}
        modelSupportsImg2Video={recentModelSupportsImg2Video}
        modelSupportsFirstLastFrame={recentModelSupportsFirstLastFrame}
        modeSupportsVideoReference={recentModeSupportsVideoReference}
        onClose={() => onCloseRecent?.()}
        onFillPrompt={(p) => onFillPromptFromRecent?.(p)}
        onFillReference={(src) => onFillReferenceFromRecent?.(src)}
        onFillVideoReference={(urls) => onFillVideoReferenceFromRecent?.(urls)}
        onUseResultAsVideoReference={(url) => onUseResultAsVideoReferenceFromRecent?.(url)}
        onDownload={() => onDownloadRecent?.(selectedRecentJob)}
      />
    </div>
  );
}

// ---------------- 最近作品的选中预览大卡 ----------------

interface RecentPreviewCardProps {
  job: VideoJobDetail;
  modelSupportsImg2Video: boolean;
  modelSupportsFirstLastFrame: boolean;
  modeSupportsVideoReference: boolean;
  onClose: () => void;
  onFillPrompt: (prompt: string) => void;
  onFillReference: (source: FillSourceMeta) => void;
  onFillVideoReference: (videoUrls: string[]) => void;
  onUseResultAsVideoReference: (videoUrl: string) => void;
  onDownload: () => void;
}

function RecentPreviewCard({
  job,
  modelSupportsImg2Video,
  modelSupportsFirstLastFrame,
  modeSupportsVideoReference,
  onClose,
  onFillPrompt,
  onFillReference,
  onFillVideoReference,
  onUseResultAsVideoReference,
  onDownload,
}: RecentPreviewCardProps) {
  const canFillPrompt = !!job.prompt?.trim();
  const isActive = ['pending', 'submitting', 'processing'].includes(job.status);

  const hasReferenceMaterial =
    (job.mode === 'img2video' && (job.referenceImages?.length ?? 0) > 0) ||
    (job.mode === 'first_last_frame' && (!!job.firstFrameImage || !!job.lastFrameImage));
  const hasVideoReferenceMaterial = (job.videoReferenceUrls?.length ?? 0) > 0;

  const targetModeSupported =
    job.mode === 'text2video'
      ? true
      : job.mode === 'img2video'
        ? modelSupportsImg2Video
        : modelSupportsFirstLastFrame;

  const fillReferenceDisabled = hasReferenceMaterial && !targetModeSupported;

  const referenceTooltip = !targetModeSupported
    ? job.mode === 'first_last_frame'
      ? '当前模型不支持首尾帧，请先切换模型'
      : '当前模型不支持图生视频，请先切换模型'
    : '';
  const fillVideoReferenceDisabled = hasVideoReferenceMaterial && !modeSupportsVideoReference;
  const videoReferenceTooltip = fillVideoReferenceDisabled ? '当前模式不支持参考视频，请先切换模式或模型' : '';
  const useResultAsVideoReferenceDisabled = Boolean(job.resultUrl) && !modeSupportsVideoReference;
  const useResultAsVideoReferenceTooltip = useResultAsVideoReferenceDisabled ? '当前模式不支持参考视频，请先切换模式或模型' : '';
  const previewVideoUrl = job.playUrl || job.resultUrl;
  const previewCoverUrl = job.coverProxyUrl || job.coverUrl;

  return (
    <div className="card overflow-hidden ring-1 ring-accent-blue/30">
      <div className="aspect-video bg-black relative">
        {previewVideoUrl ? (
          <video
            src={withAuthToken(previewVideoUrl)}
            poster={previewCoverUrl ? withAuthToken(previewCoverUrl) : undefined}
            controls
            preload="metadata"
            className="w-full h-full object-contain bg-black"
          />
        ) : isActive ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/70 text-slate-100">
            <Loader2 className="w-10 h-10 mb-2 animate-spin text-accent-blue" />
            <p className="text-sm font-medium">{getStatusLabel(job.status)}</p>
            <p className="mt-1 px-4 text-center text-xs leading-5 text-slate-200/80">
              当前任务仍在处理中，可继续复用提示词和参考素材。
            </p>
          </div>
        ) : (
          // 失败 / 超时 / 无结果：显示红色占位 + 已退积分提示
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-950/60 text-rose-200">
            <AlertCircle className="w-10 h-10 mb-2" />
            <p className="text-sm font-medium">
              {job.status === 'timeout' ? '生成超时' : job.status === 'failed' ? '生成失败' : '无可用视频'}
            </p>
            {job.errorMessage && (
              <p className="mt-1 max-w-[320px] px-4 text-center text-xs leading-5 text-rose-100/90">
                {job.errorMessage}
              </p>
            )}
            <p className="text-xs mt-1 text-rose-200/80">积分已退回</p>
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
          title="关闭预览"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 space-y-3">
        {job.prompt && (
          <p className="text-sm text-light-text line-clamp-3 leading-snug">{job.prompt}</p>
        )}

        <div className="flex items-center justify-between text-xs text-light-muted">
          <div className="flex items-center gap-2">
            <span>{getVideoModelById(job.model)?.name ?? job.model}</span>
            <span>·</span>
            <span>{job.duration}s</span>
            <span>·</span>
            <span>{job.resolution}</span>
            <span>·</span>
            <span>{job.aspectRatio}</span>
            <span>·</span>
            <span>{getModeLabel(job.mode)}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {canFillPrompt && (
            <button
              type="button"
              onClick={() => onFillPrompt(job.prompt!)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-100 transition hover:bg-emerald-100"
            >
              <Type className="w-3.5 h-3.5" />
              填充提示词
            </button>
          )}
          {hasReferenceMaterial && (
            <button
              type="button"
              onClick={() => {
                if (fillReferenceDisabled) return;
                onFillReference({
                  prompt: job.prompt,
                  mode: job.mode,
                  referenceImages: job.referenceImages ?? [],
                  firstFrameImage: job.firstFrameImage ?? null,
                  lastFrameImage: job.lastFrameImage ?? null,
                  videoReferenceUrls: job.videoReferenceUrls ?? [],
                });
              }}
              disabled={fillReferenceDisabled}
              title={referenceTooltip || undefined}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition ${
                fillReferenceDisabled
                  ? 'cursor-not-allowed bg-slate-100 text-slate-400 ring-slate-200'
                  : 'bg-sky-50 text-sky-700 ring-sky-100 hover:bg-sky-100'
              }`}
            >
              <Images className="w-3.5 h-3.5" />
              填充参考图
            </button>
          )}
          {hasVideoReferenceMaterial && (
            <button
              type="button"
              onClick={() => {
                if (fillVideoReferenceDisabled) return;
                onFillVideoReference(job.videoReferenceUrls ?? []);
              }}
              disabled={fillVideoReferenceDisabled}
              title={videoReferenceTooltip || undefined}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition ${
                fillVideoReferenceDisabled
                  ? 'cursor-not-allowed bg-slate-100 text-slate-400 ring-slate-200'
                  : 'bg-violet-50 text-violet-700 ring-violet-100 hover:bg-violet-100'
              }`}
            >
              <Film className="w-3.5 h-3.5" />
              填充参考视频
            </button>
          )}
          {job.resultUrl && (
            <button
              type="button"
              onClick={() => {
                if (useResultAsVideoReferenceDisabled) return;
                onUseResultAsVideoReference(job.resultUrl!);
              }}
              disabled={useResultAsVideoReferenceDisabled}
              title={useResultAsVideoReferenceTooltip || undefined}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition ${
                useResultAsVideoReferenceDisabled
                  ? 'cursor-not-allowed bg-slate-100 text-slate-400 ring-slate-200'
                  : 'bg-amber-50 text-amber-700 ring-amber-100 hover:bg-amber-100'
              }`}
            >
              <VideoIcon className="w-3.5 h-3.5" />
              作为参考视频
            </button>
          )}
          {job.resultUrl && (
            <button
              type="button"
              onClick={onDownload}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200 transition hover:bg-slate-200"
            >
              <Download className="w-3.5 h-3.5" />
              下载
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function getModeLabel(mode: VideoMode): string {
  switch (mode) {
    case 'text2video':
      return '文生视频';
    case 'img2video':
      return '图生视频';
    case 'first_last_frame':
      return '首尾帧';
    default:
      return mode;
  }
}

function getStatusLabel(status: VideoJobDetail['status']): string {
  switch (status) {
    case 'pending':
      return '排队中';
    case 'submitting':
      return '提交中';
    case 'processing':
      return '生成中';
    case 'completed':
      return '已完成';
    case 'failed':
      return '生成失败';
    case 'timeout':
      return '生成超时';
    default:
      return status;
  }
}
